/**
 * Created by Ivan on 11.03.2026
 */

/* eslint-disable arrow-body-style */

const Prefab = function () {
    throw new Error("Prefab is not constructable, use Prefab.instantiate(config, params)");
};

const BoundParam = function (value, context) {
    this._value = value;
    this._context = context;
};

const PrefabBuilder = {};

PrefabBuilder.instantiate = function (config, params = {}) {
    let source;

    if (config instanceof VirtualJson) {
        source = config.resolve();
        config = source.getJson();

        if (!config.isUpdatedSize) {
            config.isUpdatedSize = true;
            PrefabBuilder.resolveSize(config);
        }
    }

    // support for old format
    if (config.content === undefined || config.type !== undefined) {
        config = { content: config };
    }

    const context = {
        params: {},
        propertySources: {},
        // kept for error messages - a node name alone does not say which prefab file it came from
        source
    };

    if (params.editing) {
        context.editing = true;
        delete params.editing;
    }

    if (context.editing) {
        config = cleverapps.prefabEditor.prepareShadowNodes(config);
    }

    PrefabBuilder.prepareParams(config.params, params, context);

    const node = PrefabBuilder.buildNode(config.content, context);
    node._prefabSource = source;
    node._isPrefab = true;
    return node;
};

PrefabBuilder.buildNode = function (config, context) {
    context = { ...context };
    context.config = config;
    context.propertySources = { ...context.propertySources };

    const components = [];

    if (context.editing) {
        components.push(new ShadowNodeBridge(config, context));
    }

    for (const componentName in config.components) {
        const Component = Prefab.COMPONENTS[componentName];
        if (typeof Component !== "function") {
            throw new Error(`Prefab component "${componentName}" is not registered (node ${PrefabBuilder.describeNode(context)})`);
        }

        const component = new Component(config.components[componentName], context);
        components.push(component);
        context.propertySources[componentName] = component;

        if (componentName === "Repeater") {
            context.repeater = component;
        }
    }

    const primitive = Prefab.PRIMITIVES[config.type] || Prefab.PRIMITIVES.node;
    const node = primitive.factory(config, context);
    if (!node) {
        return;
    }

    PrefabBuilder.applyProperties(node, primitive.type, config, context);

    if (!["scene", "layout", "flexbox", "scrollview", "grid", "wrapGridLayout"].includes(config.type)) {
        PrefabBuilder.createChildren(config, context, node);
    }

    components.forEach((component) => {
        ComponentMap.attach(node, component);
    });

    return node;
};

PrefabBuilder.applyProperties = function (object, type, config, context) {
    const properties = Prefab.PROPERTIES[type];

    Object.keys(properties).forEach((key) => {
        const declaration = properties[key];

        if (!declaration.setter) {
            return;
        }

        if (config[key] !== undefined) {
            declaration.setter(object, config[key], context);
        } else if (declaration.default !== undefined) {
            declaration.setter(object, declaration.default, context);
        } else if (context.editing && declaration.editorDefault !== undefined) {
            declaration.setter(object, declaration.editorDefault, context);
        }
    });
};

PrefabBuilder.describeNode = function (context) {
    const config = context && context.config;
    const name = config && (config.name || config.type) || "unnamed";
    const source = context && context.source;
    return source ? `"${name}" of ${source}` : `"${name}"`;
};

// A node declaring "if" is built by whoever owns it - createChildren for children, the button primitive
// for its content - since a falsy condition means the node is not created at all.
PrefabBuilder.checkCondition = function (config, context) {
    return !config.if || Boolean(PrefabBuilder.resolveProperty(config.if, context));
};

// A parent takes each child as it is built, not once they all are, so a child can measure the
// siblings declared before it - the up menu leads every scene prefab for exactly that reason.
PrefabBuilder.createChildren = function (config, context, parent) {
    const children = [];

    if (!config || !config.children || !config.children.length) {
        return children;
    }

    if (context.repeater) {
        const items = context.repeater.build(config, context);
        if (parent) {
            items.forEach((item) => parent.addChild(item));
        }
        return items;
    }

    config.children.forEach((childConfig) => {
        if (context.editing) {
            childConfig = cleverapps.prefabEditor.getShadowNode(childConfig).serializeContent();
        }

        const childContext = {
            ...context,
            mapFunc: undefined
        };

        if (!PrefabBuilder.checkCondition(childConfig, childContext)) {
            return;
        }

        const itemNode = PrefabBuilder.buildNode(childConfig, childContext);
        if (itemNode) {
            if (parent) {
                parent.addChild(itemNode);
            }
            children.push(context.mapFunc ? context.mapFunc(itemNode, childConfig) : itemNode);
        }
    });

    return children;
};

PrefabBuilder.prepareParams = function (declarations, values, context) {
    declarations = PrefabBuilder.bindParams(declarations || {}, context);
    values = values || {};

    for (const key in declarations) {
        const value = values[key];
        context.params[key] = value === undefined ? declarations[key] : value;
    }

    context.themeDefault = declarations.theme;

    if (values.theme !== undefined) {
        // A theme named by a prefab node arrives bound to the context that named it, unlike one passed
        // straight from js - either way what the prefab reads is the resolved json, not the resource.
        let theme = PrefabBuilder.unwrapParameter(values.theme, context).value;

        if (theme instanceof VirtualJson) {
            theme = theme.resolve().getJson();

            if (!theme.isUpdatedSize) {
                theme.isUpdatedSize = true;
                PrefabBuilder.resolveSize(theme);
            }
        }

        context.params.theme = theme;
    }
};

PrefabBuilder.bindParams = function (params, context) {
    if (!params) {
        return params;
    }

    if (Array.isArray(params)) {
        return params.map((element) => new BoundParam(element, context));
    }

    if (typeof params === "object") {
        return Object.keys(params).reduce((result, key) => {
            result[key] = new BoundParam(params[key], context);
            return result;
        }, {});
    }

    return params;
};

PrefabBuilder.unwrapParameter = function (value, context) {
    if (value instanceof BoundParam) {
        return PrefabBuilder.unwrapParameter(value._value, value._context);
    }

    if (PrefabBuilder.isProperty(value)) {
        const path = value.slice(1).split(".");

        if (path[0] === "theme") {
            path.unshift("params");
        }

        if (path[0] === "params" || path[0] === "item") {
            let unwrapped = PrefabBuilder.walkParameter(path, context, value);

            if (unwrapped.value === undefined) {
                if (path[0] === "params" && path[1] === "theme") {
                    path.splice(0, 2, "themeDefault");
                    unwrapped = PrefabBuilder.walkParameter(path, context, value);
                }

                if (unwrapped.value === undefined && !unwrapped.found) {
                    throw new Error(`Unknown reference "${path.join(".")}"`);
                }
            }

            return unwrapped;
        }
    }
    return { value, context };
};

PrefabBuilder.walkParameter = function (path, context, reference) {
    let param = context;

    for (let i = 0; ; i++) {
        if (param === undefined || param === null || i === path.length) {
            return { value: param, context, found: i === path.length };
        }

        if (PrefabBuilder.isProperty(param)) {
            throw new Error(`Reference "${reference}" continues after resolver "#${path.slice(0, i).join(".")}" = "${param}", a resolver must be the last part of a chain`);
        }

        param = param[path[i]];

        if (param instanceof BoundParam) {
            const unwrapped = PrefabBuilder.unwrapParameter(param._value, param._context);
            param = unwrapped.value;
            context = unwrapped.context;
        }
    }
};

PrefabBuilder.isProperty = function (value) {
    return typeof value === "string" && value.startsWith("#");
};

PrefabBuilder.resolveProperty = function (value, context) {
    const unwrapped = PrefabBuilder.unwrapParameter(value, context);
    value = unwrapped.value;
    context = unwrapped.context;

    if (!PrefabBuilder.isProperty(value)) {
        return value;
    }

    const path = value.slice(1).split(".");
    const [componentName, method] = path;
    const component = context.propertySources[componentName];
    return component[method](context);
};

PrefabBuilder.resolveText = function (value, context) {
    return PrefabBuilder.localize(PrefabBuilder.resolveProperty(value, context));
};

PrefabBuilder.localize = function (value) {
    return value && value.localize !== undefined ? Messages.get(value.localize) : value;
};

PrefabBuilder.resolveSrc = function (value, context = {}) {
    const unwrapped = PrefabBuilder.unwrapParameter(value, context);
    value = unwrapped.value;
    context = unwrapped.context;

    if (PrefabBuilder.isProperty(value)) {
        value = PrefabBuilder.resolveProperty(value, context);
    }

    if (value instanceof VirtualResource) {
        return value;
    }

    if (value) {
        if (value.frame) {
            return bundles[PrefabBuilder.resolveProperty(value.bundle, context)].frames[value.frame];
        }
        if (value.json) {
            return bundles[PrefabBuilder.resolveProperty(value.bundle, context)].jsons[value.json];
        }
        if (value.url) {
            return bundles[PrefabBuilder.resolveProperty(value.bundle, context)].urls[value.url];
        }
        if (value.res) {
            return value.res;
        }
    }
    return value;
};

PrefabBuilder.serializeSrc = function (resource) {
    if (!(resource instanceof VirtualResource)) {
        return undefined;
    }

    if (resource instanceof VirtualJson) {
        return { bundle: resource.bundleName, json: resource.key };
    }

    if (resource instanceof VirtualUrl || resource instanceof SoundFrame) {
        return { bundle: resource.bundleName, url: resource.key };
    }

    return { bundle: resource.bundleName, frame: resource.key };
};

PrefabBuilder.resolveButtonType = function (value, context) {
    const buttonType = PrefabBuilder.resolveProperty(value, context);

    if (!buttonType) {
        return undefined;
    }

    if (typeof buttonType === "string") {
        return cleverapps.styles.UI.Button.Images[buttonType];
    }

    return Object.keys(buttonType).reduce((type, key) => {
        type[key] = PrefabBuilder.resolveSrc(buttonType[key], context);
        return type;
    }, {});
};

PrefabBuilder.resolveCallback = function (value, context = {}) {
    const unwrapped = PrefabBuilder.unwrapParameter(value, context);
    value = unwrapped.value;
    context = unwrapped.context;

    if (!PrefabBuilder.isProperty(value)) {
        return value;
    }

    const path = value.slice(1).split(".");
    const [componentName, method] = path;
    const component = context.propertySources[componentName];
    return component[method].bind(component, context);
};

PrefabBuilder.resolveNode = function (value, context, params) {
    const unwrapped = PrefabBuilder.unwrapParameter(value, context);
    value = unwrapped.value;
    context = unwrapped.context;

    if (PrefabBuilder.isProperty(value)) {
        value = PrefabBuilder.resolveProperty(value, context);
    }

    if (value instanceof cc.Node) {
        return value;
    }

    if (!value) {
        return undefined;
    }

    if (!cleverapps.isEmpty(params)) {
        value = { ...value, params: { ...value.params, ...params } };
    }

    return PrefabBuilder.buildNode(value, context);
};

PrefabBuilder.resolveFont = function (f, context = {}) {
    const unwrapped = PrefabBuilder.unwrapParameter(f, context);
    f = unwrapped.value;
    context = unwrapped.context;

    if (PrefabBuilder.isProperty(f)) {
        f = PrefabBuilder.resolveProperty(f, context);
    }

    const resolved = typeof f === "string" ? cleverapps.styles.FONTS[f] : { ...f };
    if (resolved.font) {
        resolved.font = PrefabBuilder.resolveSrc(resolved.font, context);
    }
    if (typeof resolved.color === "string") {
        resolved.color = PrefabBuilder.resolveColor(resolved.color, context);
    } else if (Array.isArray(resolved.color)) {
        resolved.color = new cc.Color(...resolved.color);
    }
    if (typeof resolved.stroke === "string") {
        resolved.stroke = cleverapps.styles.DECORATORS[resolved.stroke];
    }
    if (resolved.stroke && resolved.stroke.color) {
        resolved.stroke = { ...resolved.stroke, color: PrefabBuilder.resolveColor(resolved.stroke.color, context) };
    }
    if (typeof resolved.shadow === "string") {
        resolved.shadow = cleverapps.styles.DECORATORS[resolved.shadow];
    }
    if (resolved.shadow && resolved.shadow.color) {
        resolved.shadow = { ...resolved.shadow, color: PrefabBuilder.resolveColor(resolved.shadow.color, context) };
    }
    return resolved;
};

PrefabBuilder.resolveColor = function (color, context = {}) {
    color = PrefabBuilder.resolveProperty(color, context);

    if (typeof color === "string") {
        if (color.startsWith("0x")) {
            return cc.hexToColor(color);
        }

        return cleverapps.styles.COLORS[color];
    }

    if (Array.isArray(color)) {
        return new cc.Color(...color);
    }

    return color;
};

PrefabBuilder.resolveIcons = function (icons, context) {
    if (PrefabBuilder.isProperty(icons)) {
        return PrefabBuilder.resolveProperty(icons, context);
    }

    const resolved = {};
    for (const key in icons) {
        resolved[key] = PrefabBuilder.resolveSrc(icons[key], context);
    }

    return resolved;
};

PrefabBuilder.resolveSize = function (value) {
    if (typeof value === "number") {
        return Math.round(value * resolutionScale);
    }

    if (typeof value === "object") {
        for (const i in value) {
            if (cleverapps.UI.needUpdateSizes(value, i, value[i])) {
                value[i] = PrefabBuilder.resolveSize(value[i]);
            }
        }
    }

    return value;
};

Prefab.COMPONENTS = {};
Prefab.PRIMITIVES = {};
Prefab.PROPERTIES = {};

Prefab.NODE_PROPERTIES = {
    anchorPoint: {
        type: "point",
        editorGroup: "common",
        default: { x: 0.5, y: 0.5 },
        getter: (node) => node.getAnchorPoint(),
        setter: (node, value, context) => {
            const anchorPoint = PrefabBuilder.resolveProperty(value, context);
            node.setAnchorPoint(anchorPoint.x, anchorPoint.y);
        }
    },
    rotation: {
        type: "number",
        editorGroup: "common",
        getter: (node) => node.getRotation(),
        setter: (node, value, context) => node.setRotation(PrefabBuilder.resolveProperty(value, context))
    },
    contentSize: {
        type: "size",
        editorGroup: "common",
        getter: (node) => node.getContentSize(),
        setter: (node, value, context) => {
            const contentSize = PrefabBuilder.resolveProperty(value, context);
            node.setContentSize(contentSize.width || node.width, contentSize.height || node.height);
        }
    },
    zOrder: {
        type: "number",
        editorGroup: "common",
        getter: (node) => node.getLocalZOrder(),
        setter: (node, value, context) => {
            const localZOrder = PrefabBuilder.resolveProperty(value, context);
            node.setLocalZOrder(localZOrder);
        }
    },
    position: {
        type: "point",
        editorGroup: "common",
        getter: (node) => node.getPosition(),
        setter: (node, value, context) => {
            const position = PrefabBuilder.resolveProperty(value, context);

            if (Array.isArray(position) || typeof position.x === "object" || typeof position.y === "object") {
                throw new Error(`node ${PrefabBuilder.describeNode(context)}: position is aligned or per mode, declare AlignmentComponent`);
            }

            node.setPosition(position.x, position.y);
        }
    },
    scale: {
        type: "point",
        editorGroup: "common",
        getter: (node) => ({ x: node.scaleX, y: node.scaleY }),
        setter: (node, value, context) => {
            const scale = PrefabBuilder.resolveProperty(value, context);

            if (Array.isArray(scale)) {
                throw new Error(`node ${PrefabBuilder.describeNode(context)}: scale is per mode, declare ScaleComponent`);
            }

            if (typeof scale === "object") {
                node.setScale(scale.x, scale.y);
                node.baseScaleX = scale.x;
                node.baseScaleY = scale.y;
            } else {
                node.setScale(scale);
                node.baseScale = node.scale;
            }
        }
    },
    layoutOffset: {
        type: "point",
        editorGroup: "common",
        setter: (node, value, context) => {
            node.layoutOffset = PrefabBuilder.resolveProperty(value, context);
        }
    },
    fitToBox: {
        type: "size",
        editorGroup: "common",
        setter: (node, value, context) => {
            const fitToBox = PrefabBuilder.resolveProperty(value, context);
            cleverapps.UI.fitToBox(node, fitToBox);
        }
    },
    cascadeOpacity: {
        type: "bool",
        editorGroup: "common",
        setter: (node, value, context) => {
            const cascadeOpacity = PrefabBuilder.resolveProperty(value, context);
            if (cascadeOpacity) {
                node.setCascadeOpacityEnabledRecursively();
            }
        }
    },
    opacity: {
        type: "number",
        editorGroup: "common",
        getter: (node) => node.getOpacity(),
        setter: (node, value, context) => {
            const opacity = PrefabBuilder.resolveProperty(value, context);
            node.setOpacity(opacity);
        }
    },
    visible: {
        type: "bool",
        editorGroup: "common",
        getter: (node) => node.isVisible(),
        setter: (node, value, context) => {
            const visible = PrefabBuilder.resolveProperty(value, context);
            node.setVisible(visible);
        }
    },
    onClicked: {
        type: "callback",
        editorGroup: "common",
        setter: (node, value, context) => {
            if (context.config.type === "button") {
                return;
            }

            cleverapps.UI.applyHover(node, { ...context.config.options });
            cleverapps.UI.onClick(node, PrefabBuilder.resolveCallback(value, context), context.config.options);
        }
    },
    dynamicPosition: {
        type: "callback",
        editorGroup: "common",
        setter: (node, value, context) => {
            node.updateDynamicPosition = PrefabBuilder.resolveCallback(value, context);
        }
    },
    name: {
        type: "string",
        editorGroup: "common",
        getter: (node) => node.getName(),
        setter: (node, value, context) => {
            const name = PrefabBuilder.resolveProperty(value, context);
            node.setName(name);
        }
    },
    tags: {
        type: "string",
        editorGroup: "common",
        array: true,
        setter: (node, value, context) => {
            PrefabBuilder.resolveProperty(value, context).forEach((tag) => {
                node.setTag(tag);
            });
        }
    },
    debugBorder: {
        type: "bool",
        editorGroup: "common",
        setter: (node, value, context) => {
            const debugBorder = PrefabBuilder.resolveProperty(value, context);
            if (debugBorder) {
                node.debugBorder();
            } else {
                node.cleanupBorder();
            }
        }
    }
};

Prefab.isPluginComponent = function (name) {
    return /(Controller|PropertySource|Behaviou?r)$/.test(name);
};

Prefab.registerComponent = function (name, properties, constructor) {
    if (cleverapps.config.debugMode && !BundleLoader.installing && (BundleLoader.defining || Prefab.isPluginComponent(name))) {
        throw new Error(`component ${name} must be registered from plugin install`);
    }

    if (constructor === undefined) {
        constructor = properties;
        properties = undefined;
    }

    Prefab.COMPONENTS[name] = constructor;
    Prefab.PROPERTIES[name] = properties;
};

Prefab.unregisterComponent = function (name) {
    if (cleverapps.config.debugMode && !BundleLoader.uninstalling && (BundleLoader.defining || Prefab.isPluginComponent(name))) {
        throw new Error(`component ${name} must be unregistered from plugin uninstall`);
    }

    delete Prefab.COMPONENTS[name];
    delete Prefab.PROPERTIES[name];
};

Prefab.registerPrimitive = function (type, properties, factory) {
    Prefab.PRIMITIVES[type] = {
        type,
        factory
    };

    const result = {};

    Object.keys(properties).forEach((name) => {
        result[name] = properties[name];
    });

    Object.keys(Prefab.NODE_PROPERTIES).forEach((name) => {
        if (!result[name]) {
            result[name] = Prefab.NODE_PROPERTIES[name];
        }
    });

    Prefab.PROPERTIES[type] = result;
};

Prefab.instantiate = function (config, params) {
    return PrefabBuilder.instantiate(config, params);
};

Prefab.schema = function () {
    const serializeProperties = (properties) => {
        const result = {};

        for (const name in properties) {
            result[name] = {
                type: properties[name].type,
                default: properties[name].default,
                editorGroup: properties[name].editorGroup
            };
        }

        return result;
    };

    const schema = {
        nodes: {},
        components: {}
    };

    for (const type in Prefab.PRIMITIVES) {
        schema.nodes[type] = serializeProperties(Prefab.PROPERTIES[type]);
    }

    for (const name in Prefab.COMPONENTS) {
        const properties = Prefab.PROPERTIES[name];
        if (properties) {
            schema.components[name] = serializeProperties(properties);
        }
    }

    return schema;
};

Prefab.createWindow = function (bundle, json, options) {
    return function (...windowArgs) {
        options = options || {};

        const bundleName = typeof bundle === "function" ? bundle(...windowArgs) : bundle;
        const jsonKey = typeof json === "function" ? json(...windowArgs) : json;
        const theme = typeof options.theme === "function" ? options.theme(...windowArgs) : options.theme;

        const node = new cc.Node();
        node._windowArgs = windowArgs;

        node.onWindowLoaded = function (...args) {
            const config = bundles[bundleName].jsons[jsonKey];
            node.prefabNode = Prefab.instantiate(config, { theme, view: node, args });
            if (!node.name) {
                node.name = node.prefabNode.getName();
            }
            const windowNode = node.prefabNode.children[0];
            node.prefabNode.onResize = () => {
                if (windowNode && windowNode.onResize) {
                    windowNode.onResize();
                }
            };
            node.addChild(node.prefabNode);

            cc.Base.PerformRecursiveFromLeaves(node, (node) => {
                ComponentMap.callHooks(node, "onReady");
            });

            cc.Base.PerformRecursiveFromLeaves(node, (node) => {
                ComponentMap.callHooks(node, "onResize");

                if (node.onResize) {
                    node.onResize();
                }
            });

            cc.Base.PerformRecursiveFromLeaves(node, (node) => {
                ComponentMap.callHooks(node, "onAfterResize");

                if (node.onAfterResize) {
                    node.onAfterResize();
                }
            });
        };

        node.onResize = () => {
            if (node.prefabNode) {
                node.prefabNode.onResize();
            }
        };

        const loadingBundles = [bundleName];

        if (options.bundles) {
            loadingBundles.push(...(typeof options.bundles === "function" ? options.bundles(...windowArgs) : options.bundles));
        }

        const loadingTasks = [];

        if (options.loadingTasks) {
            loadingTasks.push(...(typeof options.loadingTasks === "function" ? options.loadingTasks(...windowArgs) : options.loadingTasks));
        }

        ComponentMap.attach(node, new WindowComponent(loadingBundles, loadingTasks, options));

        return node;
    };
};

Prefab.registerPrimitive("node", {}, () => {
    return new cc.Node();
});

Prefab.registerPrimitive("sprite", {
    src: {
        type: "asset",
        editorDefault: { bundle: "prefab_editor", frame: "image_placeholder" },
        getter: (sprite) => PrefabBuilder.serializeSrc(sprite.virtualResource),
        setter: (sprite, value, context) => {
            const spriteFrame = PrefabBuilder.resolveSrc(value, context);
            if (spriteFrame instanceof VirtualUrl) {
                sprite.initWithFile(spriteFrame);
            } else if (spriteFrame) {
                sprite.setSpriteFrame(spriteFrame);
            }
        }
    }
}, () => {
    return new cc.Sprite();
});

Prefab.registerPrimitive("scale9", {
    src: {
        type: "asset",
        editorDefault: { bundle: "prefab_editor", frame: "image_placeholder" },
        getter: (scale9) => PrefabBuilder.serializeSrc(scale9.virtualResource),
        setter: (scale9, value, context) => {
            const spriteFrame = PrefabBuilder.resolveSrc(value, context);
            if (spriteFrame instanceof VirtualUrl) {
                scale9.initWithFile(spriteFrame);
            } else if (spriteFrame) {
                scale9.setSpriteFrame(spriteFrame);
            }
        }
    },
    capInsets: {
        type: ["union", "rect"],
        values: [
            () => Object.keys(cleverapps.UI.Scale9Rect),
            undefined
        ],
        default: "TwoPixelXY",
        getter: (scale9) => scale9.getCapInsets(),
        setter: (scale9, value, context) => {
            const capInsets = PrefabBuilder.resolveProperty(value, context);
            if (typeof capInsets === "string") {
                scale9.setCapInsets(cleverapps.UI.getScale9Rect(scale9.virtualResource, cleverapps.UI.Scale9Rect[capInsets]));
            } else {
                scale9.setCapInsets(cc.rect(capInsets.x, capInsets.y, capInsets.width, capInsets.height));
            }
        }
    }
}, () => {
    return new cc.Scale9Sprite();
});

Prefab.registerPrimitive("spine", {
    src: {
        type: "asset",
        editorDefault: { bundle: "prefab_editor", json: "spine_placeholder" },
        getter: (spine) => PrefabBuilder.serializeSrc(spine.jsonName)
    },
    animation: {
        type: "animation",
        values: (spine) => (spine ? spine.listAnimations() : []),
        setter: (spine, value, context) => {
            if (context.editing) {
                if (JSON.stringify(spine._animations) === JSON.stringify(value)) {
                    return;
                }
                spine._animations = value;
                spine.clearTrack(0);
            }

            value = PrefabBuilder.resolveProperty(value, context);
            value = cleverapps.toArray(value) || [];

            value.forEach((animation, index, animations) => {
                const first = index === 0;
                const last = index === animations.length - 1;

                let name, loop;

                if (typeof animation === "string") {
                    name = animation;
                } else {
                    name = animation.name;
                    loop = animation.loop;
                }

                if (last && loop === undefined) {
                    loop = true;
                }

                if (first) {
                    spine.setAnimation(0, name, loop);
                } else {
                    spine.addAnimation(0, name, loop);
                }
            });
        }
    },
    skin: {
        type: "string",
        values: (spine) => (spine ? spine.listSkins() : []),
        getter: (spine) => spine.getSkin(),
        setter: (spine, value, context) => {
            spine.setSkin(PrefabBuilder.resolveProperty(value, context));
        }
    },
    timeScale: {
        type: "number",
        getter: (spine) => spine.getTimeScale(),
        setter: (spine, value, context) => {
            spine.setTimeScale(PrefabBuilder.resolveProperty(value, context));
        }
    }
}, (config, context) => {
    let src = config.src;
    if (!src && context.editing) {
        src = bundles.prefab_editor.jsons.spine_placeholder;
    }

    return new cleverapps.Spine(PrefabBuilder.resolveSrc(src, context));
});

Prefab.registerPrimitive("text", {
    src: {
        type: ["string", "localize"],
        editorDefault: "placeholder",
        getter: (text) => text.getString(),
        setter: (text, value, context) => {
            text.setString(PrefabBuilder.resolveText(value, context));
        }
    },
    font: {
        type: "font",
        values: () => Object.keys(cleverapps.styles.FONTS),
        setter: (text, value, context) => {
            text.setFont(PrefabBuilder.resolveFont(value, context));
        }
    },
    imageText: {
        type: "bool",
        getter: (text) => text instanceof cleverapps.UI.ImageFont
    },
    lineHeight: {
        type: "number",
        setter: (text, value, context) => {
            text.setLineHeight(PrefabBuilder.resolveProperty(value, context));
        }
    },
    dimensions: {
        type: "size",
        getter: (text) => text.getDimensions(),
        setter: (text, value, context) => {
            const dimensions = PrefabBuilder.resolveProperty(value, context);
            text.setDimensions(dimensions.width || 0, dimensions.height || 0);
        }
    },
    fixedHeight: {
        type: "bool",
        getter: (text) => text.fixedHeight,
        setter: (text, value, context) => {
            const fixedHeight = PrefabBuilder.resolveProperty(value, context);
            text.setFixedHeight(fixedHeight);
        }
    },
    fitTo: {
        type: "size",
        setter: (text, value, context) => {
            const fitTo = PrefabBuilder.resolveProperty(value, context);
            text.fitTo(fitTo.width, fitTo.height);
        }
    },
    alignment: {
        type: "string",
        values: () => ["left", "center", "right"],
        getter: (text) => text.getHorizontalAlignment
            && ["left", "center", "right"].find((alignment) => cc[`TEXT_ALIGNMENT_${alignment.toUpperCase()}`] === text.getHorizontalAlignment()),
        setter: (text, value, context) => {
            const alignment = PrefabBuilder.resolveProperty(value, context);
            if (typeof alignment === "string") {
                text.setHorizontalAlignment(cc[`TEXT_ALIGNMENT_${alignment.toUpperCase()}`]);
            } else if (typeof alignment === "number") {
                text.setHorizontalAlignment(alignment);
            }
        }
    },
    verticalAlignment: {
        type: "string",
        values: () => ["top", "center", "bottom"],
        getter: (text) => text.getVerticalAlignment
            && ["top", "center", "bottom"].find((alignment) => cc[`VERTICAL_TEXT_ALIGNMENT_${alignment.toUpperCase()}`] === text.getVerticalAlignment()),
        setter: (text, value, context) => {
            const alignment = PrefabBuilder.resolveProperty(value, context);
            if (typeof alignment === "string") {
                text.setVerticalAlignment(cc[`VERTICAL_TEXT_ALIGNMENT_${alignment.toUpperCase()}`]);
            } else if (typeof alignment === "number") {
                text.setVerticalAlignment(alignment);
            }
        }
    }
}, (config, context) => {
    const font = PrefabBuilder.resolveFont(config.font, context);

    if (config.imageText) {
        return cleverapps.UI.generateImageText("", font, {});
    }

    return cleverapps.UI.generateOnlyText("", font);
});

Prefab.registerPrimitive("pattern", {
    src: {
        type: "asset"
    },
    size: {
        type: "size"
    }
}, (config, context) => {
    let src = config.src;
    if (!src && context.editing) {
        src = bundles.prefab_editor.frames.image_placeholder;
    }

    return cleverapps.UI.createPatternSprite(PrefabBuilder.resolveSrc(src, context), PrefabBuilder.resolveProperty(config.size, context));
});

Prefab.registerPrimitive("clip", {
    clipping: {
        type: "bool",
        default: true,
        getter: (node) => node.isClippingEnabled(),
        setter: (node, value, context) => {
            const enabled = PrefabBuilder.resolveProperty(value, context);
            node.setClippingEnabled(enabled);
        }
    }
}, () => {
    return new cc.ClippingNode();
});

Prefab.registerPrimitive("scene", {}, () => {
    return new cc.Scene();
});

Prefab.registerPrimitive("textWithIcon", {}, (config, context) => {
    let src = config.src;
    if (!src && context.editing) {
        src = "placeholder";
    }

    const font = PrefabBuilder.resolveFont(config.font, context);
    const text = PrefabBuilder.resolveText(src, context) || "";

    const icons = config.icons ? PrefabBuilder.resolveIcons(config.icons, context) : {};

    const textWithIcon = new TextWithIcon(text, {
        ...config.options,
        font,
        icons
    });

    if (config.fitTo) {
        const fitTo = PrefabBuilder.resolveProperty(config.fitTo, context);
        textWithIcon.fitTo(fitTo.width, fitTo.height);
    }

    return textWithIcon;
});

Prefab.registerPrimitive("button", {}, (config, context) => {
    const buttonOptions = {
        type: PrefabBuilder.resolveButtonType(config.buttonType, context),
        text: PrefabBuilder.resolveText(config.text, context),
        width: config.width && PrefabBuilder.resolveProperty(config.width, context),
        height: config.height && PrefabBuilder.resolveProperty(config.height, context),
        onClicked: PrefabBuilder.resolveCallback(config.onClicked, context)
    };
    if (config.font) {
        buttonOptions.font = PrefabBuilder.resolveFont(config.font, context);
    }
    if (config.disabled !== undefined) {
        buttonOptions.disabled = PrefabBuilder.resolveProperty(config.disabled, context);
    }
    if (config.icons) {
        buttonOptions.icons = config.icons ? PrefabBuilder.resolveIcons(config.icons, context) : {};
    }
    if (config.iconScale !== undefined) {
        buttonOptions.iconScale = PrefabBuilder.resolveProperty(config.iconScale, context);
    }
    if (config.content && PrefabBuilder.checkCondition(config.content, context)) {
        buttonOptions.content = PrefabBuilder.buildNode(config.content, context);
    }
    if (config.contentOff && PrefabBuilder.checkCondition(config.contentOff, context)) {
        buttonOptions.contentOff = PrefabBuilder.buildNode(config.contentOff, context);
    }
    return new cleverapps.UI.Button(buttonOptions);
});

Prefab.registerPrimitive("timer", {}, (config, context) => {
    const timerOptions = {};
    if (config.src) {
        timerOptions.timerBg = PrefabBuilder.resolveSrc(config.src, context);
    }
    if (config.timerIcon) {
        timerOptions.timerIcon = PrefabBuilder.resolveSrc(config.timerIcon, context);
    }
    if (config.iconScale !== undefined) {
        timerOptions.iconScale = config.iconScale;
    }
    if (config.font) {
        timerOptions.font = PrefabBuilder.resolveFont(config.font, context);
    }
    if (config.timerPosition) {
        timerOptions.timerPosition = config.timerPosition;
    }
    if (config.margin !== undefined) {
        timerOptions.margin = config.margin;
    }
    if (config.timerEndText) {
        timerOptions.timerEndText = PrefabBuilder.resolveText(config.timerEndText, context);
    }

    const timeLeft = PrefabBuilder.resolveProperty(config.timeLeft, context) || 0;
    const onFinish = PrefabBuilder.resolveCallback(config.onFinish, context);

    if (config.width) {
        timerOptions.width = config.width;
    }
    if (config.showHours !== undefined) {
        timerOptions.showHours = config.showHours;
    }
    const timer = new Timer(timeLeft, onFinish, timerOptions);
    if (config.fitTo) {
        timer.fitTo(config.fitTo[0], config.fitTo[1]);
    }
    return timer;
});

Prefab.registerPrimitive("countdown", {}, (config, context) => {
    const countdownStyle = {};
    if (config.font) {
        countdownStyle.font = PrefabBuilder.resolveFont(config.font, context);
    }
    if (config.showHours !== undefined) {
        countdownStyle.showHours = config.showHours;
    }
    if (config.timerPosition) {
        countdownStyle.timerPosition = config.timerPosition;
    }
    if (config.icon) {
        countdownStyle.icon = { frame: PrefabBuilder.resolveSrc(config.icon, context) };
        if (config.iconScale !== undefined) {
            countdownStyle.icon.scale = PrefabBuilder.resolveProperty(config.iconScale, context);
        }
    }
    if (config.background) {
        countdownStyle.background = {
            width: PrefabBuilder.resolveProperty(config.background.width, context),
            height: PrefabBuilder.resolveProperty(config.background.height, context),
            frame: PrefabBuilder.resolveSrc(config.background.frame, context)
        };
    }
    if (config.dimensions) {
        countdownStyle.dimensions = PrefabBuilder.resolveProperty(config.dimensions, context);
    }
    const timeLeft = PrefabBuilder.resolveProperty(config.timeLeft, context) || 0;
    const onFinish = PrefabBuilder.resolveCallback(config.onFinish, context);
    let countDownView;
    // A countdown the model already keeps running is named instead of declared, so the view follows the
    // same object the model resets - a declared one gets a countdown of its own.
    const countDown = PrefabBuilder.resolveProperty(config.model, context) || new cleverapps.CountDown(timeLeft, {
        onFinish: onFinish ? () => onFinish(countDownView) : undefined
    });
    countDownView = new cleverapps.CountDownView(countDown, countdownStyle, {
        timerEndText: PrefabBuilder.resolveText(config.timerEndText, context)
    });
    if (config.fitTo) {
        const fitTo = PrefabBuilder.resolveProperty(config.fitTo, context);
        countDownView.fitTo(fitTo.width, fitTo.height);
    }
    return countDownView;
});

Prefab.registerPrimitive("attention", {}, (config, context) => {
    return new Attention({
        image: PrefabBuilder.resolveSrc(config.src, context)
    });
});

Prefab.registerPrimitive("scaledProgressBar", {}, (config, context) => {
    const scaledProgressBarOptions = {};
    if (config.progress) {
        scaledProgressBarOptions.progress = PrefabBuilder.resolveSrc(config.progress, context);
    }
    if (config.background) {
        scaledProgressBarOptions.background = PrefabBuilder.resolveSrc(config.background, context);
    }

    if (config.barText) {
        const barText = PrefabBuilder.resolveProperty(config.barText, context);
        scaledProgressBarOptions.barText = { ...barText };
        if (scaledProgressBarOptions.barText.icons) {
            scaledProgressBarOptions.barText.icons = PrefabBuilder.resolveIcons(scaledProgressBarOptions.barText.icons, context);
        }
        scaledProgressBarOptions.barText.font = PrefabBuilder.resolveFont(barText.font, context);
    }

    const bar = new ScaledProgressBar(scaledProgressBarOptions);
    if (config.length) {
        bar.setLength(PrefabBuilder.resolveProperty(config.length, context));
    }
    if (config.goal !== undefined) {
        bar.setGoal(PrefabBuilder.resolveProperty(config.goal, context));
    }
    if (config.percentage !== undefined) {
        bar.setPercentage(PrefabBuilder.resolveProperty(config.percentage, context));
    }
    return bar;
});

Prefab.registerPrimitive("closeRoundButton", {}, (config, context) => {
    return new CloseRoundButton({
        onClicked: PrefabBuilder.resolveCallback(config.onClicked, context),
        styles: false
    });
});

Prefab.registerPrimitive("closeButton", {}, (config, context) => {
    return new CloseButton({
        styles: false,
        onClicked: PrefabBuilder.resolveCallback(config.onClicked, context)
    });
});

Prefab.registerPrimitive("helpButton", {}, (config, context) => {
    let type = cleverapps.styles.UI.Button.Images.help_button_transparent;

    if (config.src || config.srcOn) {
        type = {
            button_png: PrefabBuilder.resolveSrc(config.src, context),
            button_on_png: PrefabBuilder.resolveSrc(config.srcOn, context)
        };
    }

    return new cleverapps.UI.HelpButton({
        type,
        callback: PrefabBuilder.resolveCallback(config.onClicked, context)
    });
});

Prefab.registerPrimitive("layout", {}, (config, context) => {
    const childNodes = [];

    PrefabBuilder.createChildren(config, context).forEach((child) => childNodes.push(child));

    const direction = config.direction === "vertical"
        ? cleverapps.UI.VERTICAL
        : cleverapps.UI.HORIZONTAL;

    const layoutOptions = {
        direction,
        margin: PrefabBuilder.resolveProperty(config.margin, context),
        onlyVisible: config.onlyVisible,
        reversed: config.reversed
    };
    if (config.padding) {
        layoutOptions.padding = PrefabBuilder.resolveProperty(config.padding, context);
    }
    if (config.dimensions) {
        layoutOptions.dimensions = PrefabBuilder.resolveProperty(config.dimensions, context);
    }

    return new cleverapps.Layout(childNodes, layoutOptions);
});

Prefab.registerPrimitive("flexbox", {}, (config, context) => {
    context.mapFunc = (node, childConfig) => {
        const descriptor = { item: node };
        if (childConfig && childConfig.flex !== undefined) {
            descriptor.flex = childConfig.flex;
        }
        return descriptor;
    };

    const items = PrefabBuilder.createChildren(config, context);

    const direction = config.direction === "vertical"
        ? cleverapps.UI.VERTICAL
        : cleverapps.UI.HORIZONTAL;

    const flexOptions = {
        direction,
        margin: config.margin || 0
    };
    if (config.width !== undefined) {
        flexOptions.width = config.width;
    }
    if (config.height !== undefined) {
        flexOptions.height = config.height;
    }
    if (config.padding) {
        flexOptions.padding = config.padding;
    }

    return new cleverapps.FlexBox(items, flexOptions);
});

Prefab.registerPrimitive("grid", {}, (config, context) => {
    const childNodes = PrefabBuilder.createChildren(config, context);

    const gridOptions = {
        columns: PrefabBuilder.resolveProperty(config.columns, context),
        reverseRows: config.reverseRows,
        ascZOrder: config.ascZOrder
    };
    if (config.margin) {
        gridOptions.margin = PrefabBuilder.resolveProperty(config.margin, context);
    }
    if (config.padding) {
        gridOptions.padding = PrefabBuilder.resolveProperty(config.padding, context);
    }
    if (config.fitToBox) {
        gridOptions.fitToBox = PrefabBuilder.resolveProperty(config.fitToBox, context);
    }
    if (config.centerLastRow) {
        gridOptions.centerLastRow = config.centerLastRow;
    }

    return new cleverapps.GridLayout(childNodes, gridOptions);
});

Prefab.registerPrimitive("wrapGridLayout", {}, (config, context) => {
    const childNodes = PrefabBuilder.createChildren(config, context);

    const gridOptions = {
        columns: PrefabBuilder.resolveProperty(config.columns, context),
        reverseRows: config.reverseRows,
        ascZOrder: config.ascZOrder
    };
    if (config.margin) {
        gridOptions.margin = PrefabBuilder.resolveProperty(config.margin, context);
    }
    if (config.padding) {
        gridOptions.padding = PrefabBuilder.resolveProperty(config.padding, context);
    }
    if (config.fitToBox) {
        gridOptions.fitToBox = PrefabBuilder.resolveProperty(config.fitToBox, context);
    }

    return new cleverapps.WrapGridLayout(childNodes, gridOptions);
});

Prefab.registerPrimitive("prizeBar", {}, (config, context) => {
    const options = {};

    if (config.reward !== undefined) {
        options.reward = PrefabBuilder.resolveProperty(config.reward, context);
    }
    if (config.onCollect) {
        options.onCollect = PrefabBuilder.resolveCallback(config.onCollect, context);
    }
    if (config.progressBar !== undefined) {
        options.progressBar = PrefabBuilder.resolveProperty(config.progressBar, context);
    }
    if (config.prizePosition !== undefined) {
        options.prizePosition = PrefabBuilder.resolveProperty(config.prizePosition, context);
    }
    if (config.prize !== undefined) {
        options.prize = config.prize;
    }
    if (config.canClick !== undefined) {
        options.canClick = config.canClick;
    }

    return new PrizeBarComponent(options);
});

Prefab.registerPrimitive("rewardsList", {}, (config, context) => {
    const rewards = PrefabBuilder.resolveProperty(config.src, context);
    const options = { ...PrefabBuilder.resolveProperty(config.options, context) };
    for (const key in options) {
        options[key] = PrefabBuilder.resolveProperty(options[key], context);
    }
    if (options.event) {
        options.event = cleverapps.EVENTS.EARN[options.event];
    }

    if (options.iconBg) {
        options.iconBg.image = PrefabBuilder.resolveSrc(options.iconBg.image, context);
    }

    if (options.textBg) {
        options.textBg.image = PrefabBuilder.resolveSrc(options.textBg.image, context);
    }

    if (options.icons) {
        const icons = {};
        for (const key in options.icons) {
            icons[key] = PrefabBuilder.resolveSrc(options.icons[key], context);
        }
        options.icons = icons;
    }

    if (options.font) {
        options.font = PrefabBuilder.resolveFont(options.font, context);
    }

    if (options.textDirection === "horizontal") {
        options.textDirection = cleverapps.UI.HORIZONTAL;
    } else if (options.textDirection === "vertical") {
        options.textDirection = cleverapps.UI.VERTICAL;
    }
    return new RewardsListComponent(rewards, options);
});

Prefab.registerPrimitive("scrollview", {}, (config, context) => {
    const children = [];

    PrefabBuilder.createChildren(config, context).forEach((child) => children.push(child));

    if (children.length > 1) {
        cleverapps.throwAsync("ScrollView can have only one child");
    }

    let direction;
    if (config.direction === "vertical") {
        direction = cleverapps.UI.ScrollView.DIR_VERTICAL;
    }
    if (config.direction === "horizontal") {
        direction = cleverapps.UI.ScrollView.DIR_HORIZONTAL;
    }
    const scrollOptions = {
        scrollBarEnabled: config.scrollBarEnabled !== false,
        mouseScroll: config.mouseScroll !== false,
        dock: config.dock !== false,
        clipping: config.clipping !== false,
        direction,
        childrenVisibility: config.childrenVisibility,
        outOfBoundaryScale: config.outOfBoundaryScale
    };

    if (config.customScrollbar) {
        scrollOptions.customScrollbar = {
            ...config.customScrollbar,
            image: PrefabBuilder.resolveSrc(config.customScrollbar.image, context)
        };
    }

    const scrollView = new cleverapps.UI.ScrollView(children.length ? children[0] : new cc.Node(), scrollOptions);

    if (config.contentSize) {
        const contentSize = PrefabBuilder.resolveProperty(config.contentSize, context);
        scrollView.setContentSize(contentSize.width || scrollView.width, contentSize.height || scrollView.height);
    }

    if (config.barPadding) {
        scrollView.setBarPadding(config.barPadding);
    }

    scrollView.scrollTo(cleverapps.UI.ScrollView.SCROLLS.UPPER_LEFT);
    return scrollView;
});

Prefab.registerPrimitive("table", {}, (config, context) => {
    const table = PrefabBuilder.resolveProperty(config.src, context);

    const options = config.options || {};

    const tableView = new cleverapps.UI.ScrollView(new cc.Node(), {
        scrollBarEnabled: !table.scrollDisabled,
        mouseScroll: !table.scrollDisabled,
        dock: options.dock || { toEnd: false }
    });

    const handlerOptions = {
        id: PrefabBuilder.resolveProperty(config.id, context),
        height: options.height,
        rowConstructor: PrefabBuilder.resolveProperty(config.rowConstructor, context),
        rowOptions: PrefabBuilder.resolveProperty(config.rowOptions, context) || {}
    };

    if (config.margin !== undefined) {
        handlerOptions.styles = { ...cleverapps.styles.DefaultTableStyles, margin: config.margin };
    }

    if (config.styles) {
        handlerOptions.styles = PrefabBuilder.resolveProperty(config.styles, context);
    }

    if (config.animations) {
        handlerOptions.animations = PrefabBuilder.resolveProperty(config.animations, context);
    }

    if (config.scroll) {
        handlerOptions.scroll = cleverapps.UI.ScrollView.SCROLLS[config.scroll];
    }

    if (config.countOfRowsToSnap !== undefined) {
        handlerOptions.countOfRowsToSnap = config.countOfRowsToSnap;
    }

    new TableHandler(table, tableView, handlerOptions);
    return tableView;
});

Prefab.registerPrimitive("drawNode", {
    color: { type: "color", default: [255, 255, 255, 255] }
}, (config, context) => {
    const draw = new cc.DrawNode();
    draw.setAnchorPoint(0, 0);
    if (config.color && config.contentSize) {
        const c = PrefabBuilder.resolveProperty(config.color, context);
        draw.drawRect(
            cc.p(0, 0),
            cc.p(config.contentSize.width, config.contentSize.height),
            cc.color(c[0], c[1], c[2], c[3] !== undefined ? c[3] : 255),
            0,
            null
        );
    }
    return draw;
});

Prefab.registerPrimitive("nodeFromSource", {}, (config, context) => {
    const params = PrefabBuilder.resolveProperty(config.params, context);
    return PrefabBuilder.resolveNode(config.src, context, PrefabBuilder.bindParams(params, context));
});

Prefab.registerPrimitive("prefab", {}, (config, context) => {
    let prefabConfig;

    if (config.src) {
        prefabConfig = PrefabBuilder.resolveSrc(config.src, context);
    }

    if (!prefabConfig) {
        cleverapps.throwAsync(`Prefab not found: ${JSON.stringify(config.src)}`);
        return;
    }

    const params = { ...PrefabBuilder.resolveProperty(config.params, context) };

    if (params.theme === undefined && context.params.theme !== undefined) {
        params.theme = context.params.theme;
    }

    // A theme named right in the json arrives as a bundle descriptor - an inherited one is already
    // the resolved theme, and must not go through resolveSrc, which would read its own keys as one.
    if (params.theme && params.theme.bundle) {
        params.theme = PrefabBuilder.resolveSrc(params.theme, context);
    }

    return Prefab.instantiate(prefabConfig, PrefabBuilder.bindParams(params, context));
});

Prefab.registerPrimitive("tabs", {}, (config, context) => {
    const direction = config.direction === "vertical"
        ? cleverapps.UI.VERTICAL
        : cleverapps.UI.HORIZONTAL;

    const tabsConfig = {
        direction,
        ...config.options
    };

    if (tabsConfig.enabledTabFont) {
        tabsConfig.enabledTabFont = PrefabBuilder.resolveFont(tabsConfig.enabledTabFont, context);
    }

    if (tabsConfig.disabledTabFont) {
        tabsConfig.disabledTabFont = PrefabBuilder.resolveFont(tabsConfig.disabledTabFont, context);
    }

    if (tabsConfig.type) {
        const resolvedType = {};
        for (const key in tabsConfig.type) {
            resolvedType[key] = PrefabBuilder.resolveSrc(tabsConfig.type[key], context);
        }
        tabsConfig.type = resolvedType;
    }

    const items = PrefabBuilder.resolveProperty(config.items, context);
    const localizedItems = Array.isArray(items) ? [] : {};
    Object.keys(items).forEach((key) => {
        localizedItems[key] = { ...items[key], text: PrefabBuilder.resolveText(items[key].text, context) };
    });

    const tabs = new Tabs(localizedItems, tabsConfig);

    if (config.onTabChanged) {
        tabs.onTabChanged = PrefabBuilder.resolveCallback(config.onTabChanged, context);
    }

    return tabs;
});